<template>
  <div class="z-0 size-full bg-gpx_gray-700" id="map" ref="mapContainer" />
</template>

<script setup lang="ts">
import * as _ from 'lodash';
import {
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
  computed,
} from 'vue';
import { useStore } from 'vuex';
import L from 'leaflet';
import 'leaflet.markercluster';
import { mapCenter } from '@/config/constants';
import * as markerUtils from './utils/marker';
import * as shapeUtils from './utils/shape';
import * as layerUtils from '@/utils/map/layer';
import { useMapResize } from '@/utils/map/resize';
import { useAiActions } from '@/utils/map/aiActions.ts';
import {
  addOverlayIfEnabled,
  createOverlayPane,
  renderMapOverlays,
} from '@/utils/map/overlay';
import { getAccountMapOverlays } from '@gpx-spa/queries';

// Types
interface BleAnchor {
  id: string;
  lat: number;
  lng: number;
  radius?: number;
}

// Extend Leaflet types
declare module 'leaflet' {
  interface MarkerClusterGroup extends L.Layer {
    setZIndex(zIndex: number): void;
    clearLayers(): void;
    getBounds(): L.LatLngBounds;
    addLayer(layer: L.Layer): void;
  }
}

// Store
const store = useStore();
const accountId = computed(() => store.getters['auth/accountId']);
const bleAnchorById = (id: string) => store.getters['bleAnchor/byId'](id);
const bleAnchors = computed(() => store.getters['bleAnchor/filtered']);

// Refs
const mapContainer = ref<HTMLElement | null>(null);
const map = ref<L.Map | null>(null);
const markers = ref<L.MarkerClusterGroup | null>(null);
const shapes = ref(L.featureGroup());
const cleanupResizeObserver = ref<(() => void) | null>(null);
const unsubscribeFromAiActions = ref(null);
const dbClickTimer = ref<number | null>(null);
const mainLayers = ref<{ [key: string]: L.TileLayer }>({});
const layerControl = ref<L.Control.Layers | null>(null);

const mapOverlayGroup = L.layerGroup([]);
const { data: mapOverlaysData } = getAccountMapOverlays(accountId);

// Methods
const setupLeafletMap = () => {
  const layers = layerUtils.getLayers();
  const { id } = layerUtils.useLocalStorageLayer();
  mainLayers.value = layers;

  map.value = L.map('map', {
    layers: [layers[id]],
    center: mapCenter as L.LatLngExpression,
    zoomControl: false,
    zoom: 5,
    minZoom: 1,
  });

  L.control.zoom({ position: 'topright' }).addTo(map.value);

  createOverlayPane(map.value);

  const overlayLayers = {
    'Show overlays': mapOverlayGroup,
  };

  layerControl.value = L.control
    .layers(mainLayers.value, overlayLayers, {
      position: 'topleft',
    })
    .addTo(map.value);

  addOverlayIfEnabled(map.value, mapOverlayGroup, 'mapOverlays');

  listenLayerChange();
  listenOverlayChange();
};

const setupCluster = () => {
  // @ts-ignore - markerClusterGroup is added by the plugin
  markers.value = L.markerClusterGroup(markerUtils.clusterOptions);
  markers.value.setZIndex(1000);

  bleAnchors.value.forEach((bleAnchor: BleAnchor) => {
    createClusterShape(bleAnchor);
    createClusterMarker(bleAnchor);
  });
};

const setView = () => {
  if (!map.value || !markers.value) return;

  map.value.addLayer(markers.value);
  map.value.addLayer(shapes.value);

  const bounds = markers.value.getBounds();
  if (_.isEmpty(bounds)) return;

  map.value.fitBounds(bounds, { maxZoom: 13 });
};

const createClusterMarker = (bleAnchor: BleAnchor) => {
  if (!markers.value || !bleAnchor.lat || !bleAnchor.lng) return;

  const getBleAnchor = () => bleAnchorById(bleAnchor.id);
  const marker = markerUtils.createBleAnchor(bleAnchor);

  marker
    .bindPopup('test', {
      closeButton: false,
      minWidth: 200,
    })
    .on('mouseover', function () {
      const bleAnchor = getBleAnchor();
      const content = markerUtils.getBleAnchorPopupContent({ bleAnchor });
      this.getPopup().setContent(content);
      this.openPopup();
    })
    .on('click', () => {
      if (dbClickTimer.value !== null) return;

      dbClickTimer.value = window.setTimeout(() => {
        dbClickTimer.value = null;
      }, 200);
    })
    .on('dblclick', () => {
      if (dbClickTimer.value) {
        clearTimeout(dbClickTimer.value);
        dbClickTimer.value = null;
      }
    });

  markers.value.addLayer(marker);
};

const createClusterShape = (bleAnchor: BleAnchor) => {
  if (!bleAnchor || !shapes.value) return;

  const coordinates = {
    lat: bleAnchor.lat,
    lng: bleAnchor.lng,
  };
  const radius = bleAnchor?.radius;
  const shape = shapeUtils.createBleShape(coordinates, radius);
  shape.addTo(shapes.value);
};

const updateCurrentPositions = () => {
  if (!markers.value || !map.value) return;

  markers.value.clearLayers();
  map.value.removeLayer(markers.value);
  setupCluster();
  map.value.addLayer(markers.value);
};

const listenLayerChange = () => {
  if (!map.value) return;

  const { set } = layerUtils.useLocalStorageLayer();
  map.value.on('baselayerchange', (e) => {
    set(e.name);
  });
};

const listenOverlayChange = () => {
  if (!map.value) return;
  const { set: setMapOverlaysValue } =
    layerUtils.useLocalStorageOverlay('mapOverlays');
  map.value.on('overlayadd', (e) => {
    if (e.name === 'Show overlays') setMapOverlaysValue(true);
  });
  map.value.on('overlayremove', (e) => {
    if (e.name === 'Show overlays') setMapOverlaysValue(false);
  });
};

// Watchers
watch(
  [() => map.value, () => mapOverlaysData.value],
  ([mapInstance, overlaysData]) => {
    if (!mapInstance) return;
    renderMapOverlays(mapInstance, overlaysData || [], mapOverlayGroup);
  },
  { immediate: true },
);

watch(bleAnchors, () => {
  updateCurrentPositions();
});

// Lifecycle hooks
onMounted(() => {
  setupLeafletMap();
  setupCluster();
  setView();

  nextTick(() => {
    if (!map.value || !mapContainer.value) return;

    cleanupResizeObserver.value = useMapResize(map, mapContainer, { delay: 0 });
    unsubscribeFromAiActions.value = useAiActions(map);
  });
});

onBeforeUnmount(() => {
  if (cleanupResizeObserver.value) {
    cleanupResizeObserver.value();
    cleanupResizeObserver.value = null;
  }
  if (unsubscribeFromAiActions.value) {
    unsubscribeFromAiActions.value();
    unsubscribeFromAiActions.value = null;
  }
});
</script>
